/**
 * What each shape means, as membership functions over the eight features.
 *
 * Every rule is a list of terms, each a trapezoid over one feature. A shape's score is the weakest of its terms, so a face that fails a defining trait cannot be rescued by excelling at another: a square jaw does not make an obviously long face square.
 *
 * Scores are independent and are never normalised against each other. Four shapes may each score 8 000; none of that is a probability, and the fact that they do not sum to 10 000 is the honest part. What a score says is how well this geometry matches this rule, and nothing else.
 *
 * The numbers below come from the geometric definitions and from the canonical MediaPipe face, whose own features this package measures at `lengthToWidth ≈ 1.14` and `widestAt ≈ 0.33`. They have never been fitted to real faces, and 4C.2 does not claim they are right — only that they are explicit, versioned and reproducible.
 */
import type { FaceFeatures } from './features';
import { NAMED_SHAPES, type NamedFaceShape } from './shapes';

/** A trapezoid: 0 below `a`, rising to 1 at `b`, 1 until `c`, falling to 0 at `d`. */
export interface Trapezoid {
  a: number;
  b: number;
  c: number;
  d: number;
}

export interface Term {
  feature: keyof FaceFeatures;
  shape: Trapezoid;
  /** What this term is checking, for the report. Never shown to a user. */
  says: string;
}

export function membership(value: number, t: Trapezoid): number {
  if (value <= t.a || value >= t.d) return 0;
  if (value >= t.b && value <= t.c) return 1;
  if (value < t.b) return (value - t.a) / (t.b - t.a);
  return (t.d - value) / (t.d - t.c);
}

const term = (
  feature: keyof FaceFeatures,
  a: number,
  b: number,
  c: number,
  d: number,
  says: string,
): Term => ({
  feature,
  shape: { a, b, c, d },
  says,
});

/** Open-ended upwards: everything above `b` is fully in. */
const atLeast = (feature: keyof FaceFeatures, a: number, b: number, says: string): Term =>
  term(feature, a, b, Infinity, Infinity, says);

/** Open-ended downwards: everything below `c` is fully in. */
const atMost = (feature: keyof FaceFeatures, c: number, d: number, says: string): Term =>
  term(feature, -Infinity, -Infinity, c, d, says);

export const RULES: Record<NamedFaceShape, readonly Term[]> = {
  // Longer than wide, balanced top and middle, a jaw that narrows a little, and a soft lower outline.
  OVAL: [
    term('lengthToWidth', 1.08, 1.14, 1.3, 1.42, 'longer than wide, but not markedly so'),
    term('jawToForehead', 0.76, 0.84, 1.0, 1.08, 'jaw a little narrower than the forehead'),
    term('foreheadToWidest', 0.74, 0.82, 0.97, 1.0, 'forehead balanced with the cheeks'),
    atLeast('jawAngleDeg', 138, 152, 'no hard corner at the jaw'),
  ],

  // As wide as it is long, with curved sides and no corner anywhere.
  ROUND: [
    atMost('lengthToWidth', 1.06, 1.16, 'about as wide as it is long'),
    atLeast('sideStraightness', 0.035, 0.06, 'sides that bow outwards'),
    atLeast('jawAngleDeg', 145, 158, 'a soft, continuous jaw'),
    term('jawToForehead', 0.82, 0.9, 1.1, 1.2, 'top and bottom of similar width'),
  ],

  // Short, straight-sided and corner-jawed, with the three widths close together.
  SQUARE: [
    atMost('lengthToWidth', 1.1, 1.22, 'not much longer than wide'),
    atMost('sideStraightness', 0.03, 0.055, 'sides that run close to straight'),
    atMost('jawAngleDeg', 140, 156, 'a distinct corner at the jaw'),
    term('jawToForehead', 0.88, 0.95, 1.12, 1.22, 'forehead and jaw nearly equal'),
    atLeast('jawToWidest', 0.8, 0.88, 'a jaw almost as wide as the widest chord'),
  ],

  // Clearly longer than wide, with sides that stay nearly parallel down the face.
  OBLONG: [
    atLeast('lengthToWidth', 1.3, 1.42, 'clearly longer than it is wide'),
    atMost('sideStraightness', 0.035, 0.06, 'sides that stay nearly parallel'),
    atLeast('jawToWidest', 0.72, 0.82, 'the face does not taper much'),
  ],

  // Widest across the forehead, tapering to a narrow, pointed chin.
  HEART: [
    atMost('jawToForehead', 0.78, 0.88, 'jaw clearly narrower than the forehead'),
    atLeast('foreheadToWidest', 0.9, 0.96, 'the forehead is at or near the widest point'),
    atMost('widestAt', 0.3, 0.42, 'widest high on the face'),
    atMost('chinRoundness', 0.5, 0.66, 'the chin comes to a point'),
  ],

  // Narrow at both ends and widest across the cheekbones.
  DIAMOND: [
    atMost('foreheadToWidest', 0.8, 0.9, 'a forehead narrower than the cheekbones'),
    atMost('jawToWidest', 0.78, 0.88, 'a jaw narrower than the cheekbones'),
    term('widestAt', 0.32, 0.4, 0.6, 0.7, 'widest across the middle of the face'),
    atMost('chinRoundness', 0.56, 0.74, 'a narrowing chin'),
  ],

  // Narrow forehead widening to a heavy jaw.
  TRIANGLE: [
    atLeast('jawToForehead', 1.02, 1.12, 'jaw wider than the forehead'),
    atMost('foreheadToWidest', 0.82, 0.92, 'a narrow forehead'),
    atLeast('jawToWidest', 0.86, 0.94, 'the jaw is at or near the widest point'),
    atLeast('widestAt', 0.5, 0.62, 'widest low on the face'),
  ],
};

export interface ShapeScore {
  shape: NamedFaceShape;
  /** 0–10 000. Independent of every other shape's score; these do not sum to 10 000. */
  scoreBp: number;
  /** The term that held the score down, which is what a diagnostic view wants to show. */
  limitedBy: string;
}

/** Every shape scored against one face's features. Order is the fixed `NAMED_SHAPES` order, never the ranking. */
export function scoreShapes(features: FaceFeatures): ShapeScore[] {
  return NAMED_SHAPES.map((shape) => {
    let weakest = 1;
    let limitedBy = '';
    for (const t of RULES[shape]) {
      const m = membership(features[t.feature], t.shape);
      if (m < weakest) {
        weakest = m;
        limitedBy = t.says;
      }
    }
    return { shape, scoreBp: Math.round(weakest * 10_000), limitedBy };
  });
}
